import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import { Size } from '@ocean/types/dist/appearance'
import { common, controls } from '@ocean/ui'
import { DateUtils } from '@ocean/utils'
import { NavigationUtils, UserUtils } from 'utils'
import { ControlGroup, Event } from 'modules/Notifications/components/EventNotifications/components'
import { namespace } from 'modules/Notifications/localization'
import { EventNotificationItem, NotificationType } from 'modules/Notifications/types'
import { SimulatorTrainingType } from 'modules/simulatorTrainings/common/types'
import {
  AnimatedBanner,
  Wrapper,
  InfoIcon,
  CloseButton,
  CarouselContainer,
  JoinWrapper,
} from './EventNotifications.styled'

const { Icon } = common
const { Button, ButtonVariant } = controls

const I18_PATH = `${namespace}:notifications.eventNotification`

type EventNotificationsProps = {
  events: EventNotificationItem[] | null
  isShow: boolean
  onClose: Callback
}

const EventNotifications: React.FC<EventNotificationsProps> = props => {
  const history = useHistory()
  const { t } = useTranslation()
  const { events, isShow, onClose } = props
  const [currentEvent, setCurrentEvent] = useState<EventNotificationItem>()
  const { id, eventRole, deadline, type } = currentEvent || {}

  useEffect(() => {
    if (!events) return
    setCurrentEvent(events[0])
  }, [events])

  const deadlineDate = new Date(deadline || 0)
  const getHoursFromDeadline = DateUtils.getHoursFromNow(deadlineDate)
  const showJoinButton = getHoursFromDeadline < 24

  function handleRedirectToDetails() {
    if (type === NotificationType.SimulatorTraining) {
      const { type: role } = UserUtils.getUserAccountInfo()
      const detailsUrl = `${NavigationUtils.getSimulatorTrainingDetailsRoute(
        role,
        SimulatorTrainingType.Instructor
      )}/${id}`
      history.push(detailsUrl)
    }

    if (typeof eventRole === 'number' && id) {
      const detailsUrl = `${NavigationUtils.getEventNotificationScheduleDetailsRoute(
        eventRole
      )}/${id}`
      history.push(detailsUrl)
    }
  }

  function handleSlideChange(nextSlide: number) {
    if (!events) return
    setCurrentEvent(events[nextSlide])
  }

  const RESPONSIVE_GRID = {
    default: {
      breakpoint: { max: 10000, min: 0 },
      items: 1,
    },
  }

  return (
    <CSSTransition in={isShow} classNames='banner' timeout={1000} unmountOnExit>
      <AnimatedBanner>
        <Wrapper>
          <InfoIcon icon='info' />
          <CarouselContainer
            ssr={false}
            responsive={RESPONSIVE_GRID}
            customButtonGroup={<ControlGroup />}
            renderButtonGroupOutside
            arrows={false}
            swipeable
            dotListClass='slideshow-dots-list'
            beforeChange={handleSlideChange}
          >
            {events?.map(event => (
              <Event key={event.id} event={event} />
            ))}
          </CarouselContainer>
          <JoinWrapper>
            <Button
              size={Size.Small}
              disabled={!showJoinButton}
              variant={ButtonVariant.Warning}
              title={t(`${I18_PATH}.goToEvent`)}
              onClick={handleRedirectToDetails}
              dataTest='EventNotificationsGoToEventButton'
            />
          </JoinWrapper>
          <CloseButton onClick={onClose}>
            <Icon icon='close' />
          </CloseButton>
        </Wrapper>
      </AnimatedBanner>
    </CSSTransition>
  )
}

export default EventNotifications
